import styled from 'styled-components';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import SEO from '../components/seo/SEO';
import { useRegionalSeo } from '../hooks/useRegionalSeo';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const Content = styled.div`
  max-width: 48rem;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
`;

const LastUpdated = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 2rem;
  
  @media (min-width: 1024px) {
    font-size: 0.9375rem;
    margin-bottom: 3rem;
  }
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.foreground};
  line-height: 1.75;
  margin-bottom: 1.25rem;
  
  @media (min-width: 768px) {
    font-size: 1.0625rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
  }
`;

const List = styled.ul`
  font-size: 1rem;
  color: ${props => props.theme.colors.foreground};
  line-height: 1.75;
  margin-bottom: 1.25rem;
  padding-left: 1.5rem;
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
  }
  
  li {
    margin-bottom: 0.75rem;
  }
`;

const Terms = () => {
  const regionalSeo = useRegionalSeo('/terms');

  return (
    <PageWrapper>
      <SEO
        title="Terms of Service"
        description="Bloomwik terms of service — rules and guidelines for using our platform."
        url="/terms"
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
      />
      <Header />
      <Main>
        <Container>
          <Content>
            <PageTitle>Terms of Service</PageTitle>
            <LastUpdated>Last updated: December 10, 2024</LastUpdated>

            <Section>
              <Paragraph>
                Please read these Terms of Service carefully before using Bloomwik. By accessing or using our 
                website, you agree to be bound by these terms.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Acceptance of Terms</SectionTitle>
              <Paragraph>
                By accessing and using Bloomwik, you accept and agree to be bound by the terms and provision 
                of this agreement. If you do not agree to these terms, please do not use our website.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Use License</SectionTitle>
              <Paragraph>
                Permission is granted to temporarily access the materials on Bloomwik for personal, 
                non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, 
                and under this license you may not:
              </Paragraph>
              <List>
                <li>Modify or copy the materials</li>
                <li>Use the materials for any commercial purpose or for any public display</li>
                <li>Attempt to reverse engineer any software contained on the website</li>
                <li>Remove any copyright or other proprietary notations from the materials</li>
              </List>
            </Section>

            <Section>
              <SectionTitle>User Content</SectionTitle>
              <Paragraph>
                If you submit content to Bloomwik, you grant us a worldwide, non-exclusive, royalty-free 
                license to use, reproduce, and distribute your content. You represent that you have the right 
                to grant this license and that your content does not violate any third-party rights.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Prohibited Uses</SectionTitle>
              <Paragraph>You may not use our website:</Paragraph>
              <List>
                <li>In any way that violates any applicable law or regulation</li>
                <li>To transmit any malicious code or viruses</li>
                <li>To collect or track personal information of others</li>
                <li>To spam, phish, or engage in any fraudulent activity</li>
                <li>To interfere with or disrupt the website or servers</li>
              </List>
            </Section>

            <Section>
              <SectionTitle>Intellectual Property</SectionTitle>
              <Paragraph>
                All content on Bloomwik, including text, graphics, logos, and images, is the property of 
                Bloomwik or its content suppliers and is protected by copyright and other intellectual 
                property laws.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Disclaimer</SectionTitle>
              <Paragraph>
                The materials on Bloomwik are provided on an 'as is' basis. Bloomwik makes no warranties, 
                expressed or implied, and hereby disclaims all other warranties including, without limitation, 
                implied warranties of merchantability or fitness for a particular purpose.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Limitation of Liability</SectionTitle>
              <Paragraph>
                In no event shall Bloomwik or its suppliers be liable for any damages (including, without 
                limitation, damages for loss of data or profit, or due to business interruption) arising 
                out of the use or inability to use the materials on Bloomwik.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Changes to Terms</SectionTitle>
              <Paragraph>
                Bloomwik may revise these terms of service at any time without notice. By using this website, 
                you are agreeing to be bound by the then current version of these terms of service.
              </Paragraph>
            </Section>

            <Section>
              <SectionTitle>Contact Information</SectionTitle>
              <Paragraph>
                If you have any questions about these Terms of Service, please contact us at hello@bloomwik.com.
              </Paragraph>
            </Section>
          </Content>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Terms;

